import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { candidateApi } from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const s = {
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', marginBottom: 24 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 },
  input: { padding: '10px 14px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, width: '100%', boxSizing: 'border-box' },
  btn: { padding: '10px 24px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', background: '#f9fafb', borderBottom: '1px solid #eee' },
  td: { padding: '12px 16px', fontSize: 14, borderBottom: '1px solid #f0f0f0' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#3730a3' },
  actionBtn: (color) => ({ padding: '4px 10px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 4, cursor: 'pointer', background: color, color: '#fff', marginRight: 4 }),
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: 12, padding: 32, width: 480, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' },
};

const EMPTY = { name: '', email: '', phone: '', position: '', joiningDate: '' };

export default function CandidatesPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [editCandidate, setEditCandidate] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);

  const { data: candidates = [], isLoading } = useQuery({
    queryKey: ['candidates'],
    queryFn: () => candidateApi.list().then(r => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (data) => candidateApi.create(data),
    onSuccess: () => { qc.invalidateQueries(['candidates']); setForm(EMPTY); toast.success('Candidate added'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to add candidate'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => candidateApi.update(id, data),
    onSuccess: () => { qc.invalidateQueries(['candidates']); setEditCandidate(null); toast.success('Candidate updated'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update'),
  });

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const setEdit = (field) => (e) => setEditForm({ ...editForm, [field]: e.target.value });

  const openEdit = (c) => {
    setEditCandidate(c);
    setEditForm({ name: c.name, email: c.email, phone: c.phone || '', position: c.position || '', joiningDate: c.joiningDate || '' });
  };

  return (
    <div>
      <h1 style={s.title}>Candidates</h1>

      <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate({ ...form, joiningDate: form.joiningDate || null }); }}
        style={{ background: '#fff', borderRadius: 10, padding: 24, marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontWeight: 600, marginBottom: 16 }}>Add Candidate</h3>
        <div style={s.grid}>
          <input style={s.input} placeholder="Full Name *" required value={form.name} onChange={set('name')} />
          <input style={s.input} type="email" placeholder="Email *" required value={form.email} onChange={set('email')} />
          <input style={s.input} placeholder="Phone" value={form.phone} onChange={set('phone')} />
          <input style={s.input} placeholder="Role / Position" value={form.position} onChange={set('position')} />
          <div>
            <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Joining Date</label>
            <input style={s.input} type="date" value={form.joiningDate} onChange={set('joiningDate')} />
          </div>
        </div>
        <button style={s.btn} type="submit" disabled={createMutation.isPending}>
          {createMutation.isPending ? 'Adding...' : '+ Add Candidate'}
        </button>
      </form>

      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>ID</th><th style={s.th}>Name</th><th style={s.th}>Email</th>
            <th style={s.th}>Phone</th><th style={s.th}>Position</th><th style={s.th}>Joining Date</th><th style={s.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && <tr><td style={{ ...s.td, textAlign: 'center' }} colSpan={7}>Loading...</td></tr>}
          {candidates.map(c => (
            <tr key={c.id}>
              <td style={s.td}><span style={s.badge}>{c.displayId || '—'}</span></td>
              <td style={s.td}>{c.name}</td>
              <td style={s.td}>{c.email}</td>
              <td style={s.td}>{c.phone || '—'}</td>
              <td style={s.td}>{c.position || '—'}</td>
              <td style={s.td}>{c.joiningDate ? format(new Date(c.joiningDate), 'MMM d, yyyy') : '—'}</td>
              <td style={s.td}>
                <button style={s.actionBtn('#7c83fd')} onClick={() => openEdit(c)}>✏️ Edit</button>
              </td>
            </tr>
          ))}
          {!isLoading && candidates.length === 0 && (
            <tr><td style={{ ...s.td, textAlign: 'center', color: '#999' }} colSpan={7}>No candidates yet</td></tr>
          )}
        </tbody>
      </table>

      {editCandidate && (
        <div style={s.overlay} onClick={() => setEditCandidate(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 16 }}>Edit Candidate</h3>
            <form onSubmit={(e) => { e.preventDefault(); updateMutation.mutate({ id: editCandidate.id, data: { ...editForm, joiningDate: editForm.joiningDate || null } }); }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Name</label>
              <input style={{ ...s.input, marginBottom: 12 }} value={editForm.name} onChange={setEdit('name')} />
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Email</label>
              <input style={{ ...s.input, marginBottom: 12 }} type="email" value={editForm.email} onChange={setEdit('email')} />
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Phone</label>
              <input style={{ ...s.input, marginBottom: 12 }} value={editForm.phone} onChange={setEdit('phone')} />
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Position</label>
              <input style={{ ...s.input, marginBottom: 12 }} value={editForm.position} onChange={setEdit('position')} />
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Joining Date</label>
              <input style={{ ...s.input, marginBottom: 16 }} type="date" value={editForm.joiningDate} onChange={setEdit('joiningDate')} />
              <button style={s.btn} type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
              <button style={{ ...s.btn, background: '#fff', color: '#666', border: '1px solid #ddd', marginLeft: 8 }}
                type="button" onClick={() => setEditCandidate(null)}>Cancel</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
